class LusheTheme {
  constructor() {
    this.body = document.body;
    this.overlay = document.querySelector('[data-drawer-overlay]');
    this.cartDrawer = document.querySelector('[data-cart-drawer]');
    this.toast = document.querySelector('[data-toast]');
    this.bindGlobal();
    this.bindProductForms();
    this.bindProductGallery();
    this.bindVariantPickers();
    this.bindQuantity();
  }

  bindGlobal() {
    document.querySelectorAll('[data-mobile-menu-open]').forEach((button) => button.addEventListener('click', () => this.openDrawer('menu')));
    document.querySelectorAll('[data-cart-open]').forEach((button) => button.addEventListener('click', (event) => {
      event.preventDefault();
      this.openCart();
    }));
    document.querySelectorAll('[data-drawer-close]').forEach((button) => button.addEventListener('click', () => this.closeDrawers()));
    if (this.overlay) this.overlay.addEventListener('click', () => this.closeDrawers());

    document.querySelectorAll('[data-search-toggle]').forEach((button) => button.addEventListener('click', () => {
      const search = document.querySelector('[data-search-popover]');
      if (!search) return;
      search.classList.toggle('is-open');
      const input = search.querySelector('input');
      if (search.classList.contains('is-open') && input) input.focus();
    }));

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeDrawers();
    });
  }

  openDrawer(type) {
    this.closeDrawers(false);
    const drawer = type === 'menu' ? document.querySelector('[data-mobile-menu]') : this.cartDrawer;
    if (!drawer) return;
    drawer.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    this.body.classList.add('drawer-open');
  }

  closeDrawers(removeBody = true) {
    document.querySelectorAll('.mobile-menu.is-open,.cart-drawer.is-open').forEach((drawer) => drawer.classList.remove('is-open'));
    this.overlay?.classList.remove('is-open');
    if (removeBody) this.body.classList.remove('drawer-open');
  }

  bindProductForms() {
    document.querySelectorAll('form[data-ajax-add]').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        if (!document.documentElement.dataset.cartDrawerEnabled || document.documentElement.dataset.cartDrawerEnabled !== 'true') return;
        event.preventDefault();
        const submit = form.querySelector('[type="submit"]');
        submit?.setAttribute('disabled', 'disabled');
        try {
          const response = await fetch(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js', {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            body: new FormData(form)
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description || 'Unable to add this product.');
          await this.refreshCart();
          this.openDrawer('cart');
          this.showToast('Added to your bag');
        } catch (error) {
          this.showToast(error.message || 'Something went wrong');
        } finally {
          submit?.removeAttribute('disabled');
        }
      });
    });
  }

  async refreshCart() {
    const response = await fetch('/cart.js');
    const cart = await response.json();
    document.querySelectorAll('[data-cart-count]').forEach((count) => count.textContent = cart.item_count);
    const body = document.querySelector('[data-cart-drawer-body]');
    const subtotal = document.querySelector('[data-cart-drawer-subtotal]');
    if (subtotal) subtotal.textContent = this.formatMoney(cart.total_price);
    if (!body) return;
    if (!cart.items.length) {
      body.innerHTML = '<div class="empty-state"><h3>Your beauty bag is empty</h3><p>Add your favourites and they will appear here.</p></div>';
      return;
    }
    body.innerHTML = cart.items.map((item) => `
      <div class="drawer-cart-item">
        <img src="${item.image || ''}" alt="${this.escapeHtml(item.product_title)}" width="78" height="78">
        <div><h4>${this.escapeHtml(item.product_title)}</h4><small>${item.variant_title && item.variant_title !== 'Default Title' ? this.escapeHtml(item.variant_title) + ' · ' : ''}Qty ${item.quantity}</small><div><strong>${this.formatMoney(item.final_line_price)}</strong></div></div>
        <a href="/cart/change?id=${encodeURIComponent(item.key)}&quantity=0" aria-label="Remove ${this.escapeHtml(item.product_title)}">×</a>
      </div>`).join('');
  }

  bindProductGallery() {
    document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      const main = gallery.querySelector('[data-main-image]');
      gallery.querySelectorAll('[data-thumb]').forEach((thumb) => thumb.addEventListener('click', () => {
        if (!main) return;
        main.src = thumb.dataset.image;
        main.srcset = '';
        gallery.querySelectorAll('[data-thumb]').forEach((item) => item.classList.remove('is-active'));
        thumb.classList.add('is-active');
      }));
    });
  }

  bindVariantPickers() {
    document.querySelectorAll('[data-product-root]').forEach((root) => {
      const json = root.querySelector('[data-product-json]');
      const idInput = root.querySelector('input[name="id"]');
      if (!json || !idInput) return;
      const product = JSON.parse(json.textContent);
      const options = [...root.querySelectorAll('[data-option-index]')];
      const update = () => {
        const selected = options.map((group) => group.querySelector('input:checked')?.value);
        const variant = product.variants.find((candidate) => candidate.options.every((option, index) => option === selected[index]));
        const button = root.querySelector('[data-add-button]');
        if (!variant) return;
        idInput.value = variant.id;
        const price = root.querySelector('[data-product-price]');
        const compare = root.querySelector('[data-product-compare]');
        if (price) price.textContent = this.formatMoney(variant.price);
        if (compare) {
          compare.textContent = variant.compare_at_price > variant.price ? this.formatMoney(variant.compare_at_price) : '';
          compare.style.display = variant.compare_at_price > variant.price ? '' : 'none';
        }
        if (button) {
          button.disabled = !variant.available;
          button.textContent = variant.available ? button.dataset.availableText : button.dataset.soldText;
        }
        if (history.replaceState) history.replaceState({}, '', `${location.pathname}?variant=${variant.id}`);
      };
      options.forEach((group) => group.addEventListener('change', update));
    });
  }

  bindQuantity() {
    document.querySelectorAll('[data-quantity]').forEach((quantity) => {
      const input = quantity.querySelector('input');
      quantity.querySelector('[data-minus]')?.addEventListener('click', () => input.value = Math.max(Number(input.min || 1), Number(input.value || 1) - 1));
      quantity.querySelector('[data-plus]')?.addEventListener('click', () => input.value = Number(input.value || 1) + 1);
    });
  }

  openCart() {
    this.refreshCart().finally(() => this.openDrawer('cart'));
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: window.Shopify?.currency?.active || 'PKR' }).format(cents / 100);
    } catch (e) {
      return `Rs. ${(cents / 100).toFixed(2)}`;
    }
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value || '';
    return div.innerHTML;
  }

  showToast(message) {
    if (!this.toast) return;
    this.toast.textContent = message;
    this.toast.classList.add('is-show');
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => this.toast.classList.remove('is-show'), 2200);
  }
}

document.addEventListener('DOMContentLoaded', () => new LusheTheme());
document.addEventListener(
  'submit',
  async function (event) {
    const form = event.target.closest('form[data-ajax-add]');

    if (!form) return;

    event.preventDefault();
    event.stopPropagation();

    const button =
      event.submitter || form.querySelector('button[type="submit"]');

    const originalText = button ? button.textContent : '';

    if (button) {
      button.disabled = true;
      button.textContent = 'Adding...';
    }

    try {
      const root = window.Shopify?.routes?.root || '/';

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new FormData(form)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result.description ||
          result.message ||
          'Product cart mein add nahi ho saka.'
        );
      }

      await refreshBottanacsCart();

      const drawer = document.querySelector('[data-cart-drawer]');
      const overlay = document.querySelector('[data-drawer-overlay]');

      if (drawer) {
        drawer.classList.add('is-open');
      }

      if (overlay) {
        overlay.classList.add('is-open');
      }

      document.body.classList.add('drawer-open');

      const toast = document.querySelector('[data-toast]');

      if (toast) {
        toast.textContent = 'Product added to cart';
        toast.classList.add('is-show');

        setTimeout(function () {
          toast.classList.remove('is-show');
        }, 2200);
      }
    } catch (error) {
      alert(error.message || 'Something went wrong.');
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = originalText;
      }
    }
  },
  true
);

async function refreshBottanacsCart() {
  const root = window.Shopify?.routes?.root || '/';

  const response = await fetch(`${root}cart.js`, {
    headers: {
      Accept: 'application/json'
    }
  });

  const cart = await response.json();

  document.querySelectorAll('[data-cart-count]').forEach(function (count) {
    count.textContent = cart.item_count;
  });

  const drawerBody = document.querySelector(
    '[data-cart-drawer-body]'
  );

  const subtotal = document.querySelector(
    '[data-cart-drawer-subtotal]'
  );

  if (subtotal) {
    subtotal.textContent = formatBottanacsMoney(cart.total_price);
  }

  if (!drawerBody) return;

  if (!cart.items.length) {
    drawerBody.innerHTML = `
      <div class="empty-state">
        <h3>Your beauty bag is empty</h3>
        <p>Add your favourites and they will appear here.</p>
      </div>
    `;

    return;
  }

  drawerBody.innerHTML = cart.items
    .map(function (item) {
      const variant =
        item.variant_title &&
        item.variant_title !== 'Default Title'
          ? `${escapeBottanacsHtml(item.variant_title)} · `
          : '';

      return `
        <div class="drawer-cart-item">
          ${
            item.image
              ? `<img
                  src="${item.image}"
                  alt="${escapeBottanacsHtml(item.product_title)}"
                  width="78"
                  height="78"
                >`
              : ''
          }

          <div>
            <h4>${escapeBottanacsHtml(item.product_title)}</h4>
            <small>${variant}Qty ${item.quantity}</small>

            <div>
              <strong>
                ${formatBottanacsMoney(item.final_line_price)}
              </strong>
            </div>
          </div>

          <a
            href="/cart/change?id=${encodeURIComponent(
              item.key
            )}&quantity=0"
            aria-label="Remove ${escapeBottanacsHtml(
              item.product_title
            )}"
          >
            ×
          </a>
        </div>
      `;
    })
    .join('');
}

function formatBottanacsMoney(cents) {
  try {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'PKR'
    }).format(cents / 100);
  } catch (error) {
    return `Rs. ${(cents / 100).toFixed(2)}`;
  }
}

function escapeBottanacsHtml(value) {
  const div = document.createElement('div');
  div.textContent = value || '';
  return div.innerHTML;
}
